import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { Component } from 'react';

const CENTERING_STEP_BY_FRAME = 4;

class DateCarousel extends Component {
  constructor(props) {
    super(props);
    this.scrollLeft = this.scrollLeft.bind(this);
    this.scrollRight = this.scrollRight.bind(this);
    this.handleScroll = this.handleScroll.bind(this);
    this.handleResize = this.handleResize.bind(this);
    this.centerCurrentDate = this.centerCurrentDate.bind(this);
    this.carousel = null;
    this.state = {
      scrollRightActive: true,
      scrollLeftActive: false
    };
  }

  componentDidMount() {
    window.addEventListener('resize', this.handleResize);
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.handleResize);
  }

  scrollRight() {
    const animate = () => {
      const startPosition = this.carousel.scrollLeft;
      const scrollBy = this.carousel.scrollWidth - this.carousel.offsetWidth;
      const progress = scrollBy - startPosition;
      this.carousel.scrollLeft += Math.min(progress, 15);
      if (progress > 0) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  componentDidUpdate(prevProps) {
    if (prevProps.activeDate !== this.props.activeDate) {
      this.centerCurrentDate();
    }
  }

  centerCurrentDate() {
    const { activeDate } = this.props;
    let prevFrom = Infinity;

    if (typeof activeDate === 'number') {
      const animate = () => {
        const activeItem = this.carousel.querySelectorAll('.bb-carousel-item')[activeDate];
        const from = this.carousel.scrollLeft;
        const to = activeItem.offsetLeft - (this.carousel.getBoundingClientRect().width / 2);
        /**
         * if we havent changed scroll position since last step,
         * it means that we cant scroll further or we reached our position
         */
        if (from !== prevFrom) {
          const distance = Math.abs(from - to);
          const step = Math.min(distance, CENTERING_STEP_BY_FRAME);
          const move = (from <= to) ? step : -step;

          this.carousel.scrollLeft += move;
          prevFrom = from;
          requestAnimationFrame(animate);
        }
      };

      requestAnimationFrame(animate);
    }
  }

  scrollLeft() {
    const animate = () => {
      const startPosition = this.carousel.scrollLeft;
      const scrollBy = this.carousel.scrollWidth;
      const progress = Math.abs(scrollBy - startPosition);
      this.carousel.scrollLeft -= Math.min(progress, 15);
      if (startPosition > 0) {
        requestAnimationFrame(animate);
      }
    };

    requestAnimationFrame(animate);
  }

  handleResize() {
    if (this.carousel) {
      this.carousel.querySelector('.bb-carousel-item--active').scrollIntoView();
    }
  }

  handleScroll(event) {
    const startPosition = event.target.scrollLeft;
    const elWidth = event.target.scrollWidth;
    const parentWidth = event.target.offsetWidth;

    if (startPosition === 0) {
      this.setState({
        scrollRightActive: true,
        scrollLeftActive: false
      });
    } else if (startPosition + parentWidth === elWidth) {
      this.setState({
        scrollRightActive: false,
        scrollLeftActive: true
      });
    } else {
      this.setState({
        scrollRightActive: true,
        scrollLeftActive: true
      });
    }
  }

  render() {
    const { dates, activeDate, setDate } = this.props;
    const leftArrowClass = classNames(
      'bb-carousel-container__arrow',
      'bb-carousel-container__arrow--left',
      { 'bb-carousel-container__arrow--active': this.state.scrollLeftActive }
    );
    const rightArrowClass = classNames(
      'bb-carousel-container__arrow',
      'bb-carousel-container__arrow--right',
      { 'bb-carousel-container__arrow--active': this.state.scrollRightActive }
    );

    return (
      <div className="bb-carousel-container">
        {(dates.length > 5) && (
          <div
            className={leftArrowClass}
            onClick={this.scrollLeft}
          >
            <i className="icon icon--arrow-left" />
          </div>
        )}
        <div
          className="bb-carousel"
          ref={(ref) => {
            this.carousel = ref;
          }}
          onScroll={this.handleScroll}
        >
          <div className="bb-carousel__scrollable">
            {dates.map(((day, index) => {
              const isActive = activeDate === index;
              const itemClass = classNames(
                'bb-carousel-item',
                {
                  'bb-carousel-item--active': isActive,
                  'bb-carousel-item--nowrap': dates.length <= 3
                }
              );

              return (
                <div
                  className={itemClass}
                  key={`${day.dayName}${day.dayNumber}${day.month}`}
                  onClick={() => setDate(index)}
                >
                  <span className="bb-carousel-item__title">{day.dayName}</span>
                  <span className="bb-carousel-item__date">{day.dayNumber} {day.month}</span>
                </div>
              );
            }))}
          </div>
        </div>
        {(dates.length > 5) && (
          <div
            className={rightArrowClass}
            onClick={this.scrollRight}
          >
            <i className="icon icon--arrow-right" />
          </div>
        )}
      </div>
    );
  }
}

DateCarousel.propTypes = {
  activeDate: PropTypes.number.isRequired,
  setDate: PropTypes.func.isRequired,
  dates: PropTypes.arrayOf(PropTypes.shape({
    dayNumber: PropTypes.number.isRequired,
    dayName: PropTypes.string.isRequired,
    month: PropTypes.string.isRequired
  }).isRequired).isRequired
};

export default DateCarousel;
